import React, { useState, useEffect } from 'react';
import { ExternalLink, Lock, UserRound, Gift, Loader2, Mail, AlertTriangleIcon } from 'lucide-react';
import { Logo } from '../../common/Logo';
import { JobProtLogo } from '../../common/JobProtLogo';
import { Button } from '../../common/Button';
import { GoogleSignInButton } from '../GoogleSignInButton';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { useToast } from '../../common/Toast';
import ssoService from '../../../services/ssoService';
import googleAuthService from '../../../services/googleAuthService';
import { useAnalyticsContext } from '../../../contexts/AnalyticsContext';
import { logEvent } from 'firebase/analytics';
import { trackCustomEvent, trackAuthAttempt, trackAuthSuccess, trackAuthFailure, trackPopupBlocked, analytics } from '../../../firebaseConfig';
import { useMetaPixel } from '../../../hooks/useMetaPixel';
import apiClient from '../../../services/apiClient';
import { TokenExchangeResponse } from '../../../services/ssoService';
import { useAuthStore } from '../../../store';
import referralService from '../../../services/referralService';
import { SafariPopupGuide } from './SafariPopupGuide';
import { detectBrowser, isSafariApple } from '../../../utils/browserDetection';
import { useWeekendDetection } from '../../../hooks/useWeekendDetection';

interface LoginFormProps {
  onSSOSuccess?: (response: TokenExchangeResponse) => Promise<void>;
}

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

export function LoginForm({ onSSOSuccess }: LoginFormProps) {
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [directSignupInProgress, setDirectSignupInProgress] = useState(false);
  const [referralInfo, setReferralInfo] = useState<{
    code: string;
    name: string | null;
    isValid?: boolean;
  } | null>(null);
  const [referralLoading, setReferralLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSafariPopupGuide, setShowSafariPopupGuide] = useState(false);
  const { showToast } = useToast();
  const { trackButton, trackEvent } = useAnalyticsContext();
  const { trackPopupBlocked, trackAuthAttempt, trackAuthSuccess, trackAuthFailure } = useMetaPixel();

  const { isMobile } = useResponsive();
  const [processingOAuthCallback, setProcessingOAuthCallback] = useState(false);

  const {isWeekend} = useWeekendDetection();
  // Verify referral code with the backend
  const verifyReferralCode = async (code: string) => {
    try {
      setReferralLoading(true);
      console.log(`Verifying referral code: ${code}`);

      const referralDetails = await referralService.getReferralInfo(code);
      console.log('Referral code details:', referralDetails);

      // Check if the referral exists and get referrer details
      if (referralDetails && referralDetails.referrer) {
        // Try to get referrer's name from the response
        let referrerName = null;

        try {
          // The referrer might be populated as an object or just the ID
          if (typeof referralDetails.referrer === 'object' && referralDetails.referrer !== null) {
            // If it's an object with name property
            if (referralDetails.referrer.name) {
              referrerName = referralDetails.referrer.name;
            }
          } else {
            // If it's just the ID, additional API call may be needed to get user details
            // This would depend on your API structure
          }
        } catch (nameError) {
          console.error('Error extracting referrer name:', nameError);
        }

        // Update referral info with verified data and mark as valid
        setReferralInfo({
          code,
          name: referrerName || extractNameFromCode(code), // Fallback to extracted name if API doesn't return name
          isValid: true
        });

        // Store valid referral code
        localStorage.setItem('referralCode', code);

        console.log(`Verified referral code: ${code}, Referrer: ${referrerName || 'Unknown'}`);

        // Track successful verification
        if (trackEvent) {
          trackEvent('referral_code_verified', {
            code,
            referrer_name: referrerName,
            landing_page: 'login'
          });
        }
      } else {
        console.warn(`Invalid or expired referral code: ${code}`);

        // Update referral info with invalid status
        setReferralInfo({
          code,
          name: extractNameFromCode(code),
          isValid: false
        });

        // Store the code anyway for backend validation
        localStorage.setItem('referralCode', code);

        // Show a warning toast
        showToast('This referral code appears to be invalid, but we\'ll try to use it during signup.', 'info');
      }
    } catch (error: any) {
      console.error('Error verifying referral code:', error);

      // Check for specific error types and show appropriate messages
      if (error.response?.status === 404) {
        // Set referral as invalid
        setReferralInfo({
          code,
          name: extractNameFromCode(code),
          isValid: false
        });

        // Don't show toast for 404 - UI already displays "Invalid referral code"
        console.log('The referral code was not found');
      } else if (error.response?.status === 400 && error.response?.data?.message?.includes('expired')) {
        showToast('This referral code has expired. Please try another one.', 'error');

        // Don't use expired codes
        localStorage.removeItem('referralCode');
        setReferralInfo(null);
        return;
      } else {
        // Generic error, but still store the code anyway as invalid
        setReferralInfo({
          code,
          name: extractNameFromCode(code),
          isValid: false
        });

        localStorage.setItem('referralCode', code);

        // Only show toast for other errors
        showToast('Could not verify referral code. We\'ll still try to use it during signup.', 'info');
      }
    } finally {
      setReferralLoading(false);
    }
  };

  // Extract name from referral code (used as fallback)
  const extractNameFromCode = (code: string): string | null => {
    const parts = code.split('_');
    let name = null;

    if (parts.length > 0) {
      // Convert from snake_case to Title Case (e.g., "bhaskar_kaura" to "Bhaskar Kaura")
      name = parts[0].split('_')
        .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
        .join(' ');

      // If there's a second part before numbers, add it to the name
      if (parts.length > 1 && isNaN(Number(parts[1]))) {
        name += ' ' + parts[1].charAt(0).toUpperCase() + parts[1].slice(1).toLowerCase();
      }
    }

    return name;
  };

  // Check for referral code in URL on component mount
  useEffect(() => {
    const extractReferralFromUrl = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');
      const authCode = urlParams.get('code');
      const source = urlParams.get('source');
      const state = urlParams.get('state');

      // FIRST: Check if this is a Google OAuth callback
      if (authCode && !source) {
        // Check if we have Google auth redirect markers in sessionStorage
        const isGoogleRedirect = sessionStorage.getItem('google_auth_redirect') === 'true';
        if (isGoogleRedirect) {
          console.log('Google OAuth callback detected');
          setProcessingOAuthCallback(true);
          setGoogleLoading(true);
          
          try {
            const response = await googleAuthService.handleOAuthCallback();
            
            if (response && onSSOSuccess) {
              console.log('Google OAuth callback successful');
              
              // Track successful OAuth callback
              if (trackEvent) {
                trackEvent('google_signin_success', {
                  method: 'redirect_flow',
                  is_mobile: true
                });
              }
              
              await onSSOSuccess(response);
            }
          } catch (error: any) {
            console.error('Google OAuth callback failed:', error);
            setErrorMessage('Failed to complete Google sign-in. Please try again.');
            
            if (trackEvent) {
              trackEvent('google_signin_failure', {
                method: 'redirect_flow',
                reason: error?.message || 'callback_error'
              });
            }
          } finally {
            setProcessingOAuthCallback(false);
            setGoogleLoading(false);
          }
          
          return; // Exit early after handling OAuth callback
        }
      }

      // ENHANCED: Handle direct JobProt signup with authorization code
      if (authCode && source === 'jobprot') {
        console.log(`Direct JobProt signup detected with auth code: ${authCode.substring(0, 10)}...`);
        console.log(`State parameter from JobProt: ${state}`);

        // Track the direct signup attempt
        if (trackEvent) {
          trackEvent('direct_jobprot_signup', {
            has_referral: !!refCode,
            referral_code: refCode,
            auth_code_length: authCode.length,
            has_state: !!state
          });
        }

        // Automatically exchange the code for tokens with provided state
        await handleDirectJobProtSignup(authCode, refCode, state);
        return; // Exit early since we're handling the signup flow
      }

      // EXISTING: Regular referral code detection (no auth code)
      if (refCode && !authCode) {
        console.log(`Referral code detected in URL: ${refCode}`);

        // Track referral code detection immediately
        if (trackEvent) {
          trackEvent('referral_code_detected', {
            code: refCode,
            landing_page: 'login'
          });
        }

        // Verify the referral code with the backend
        await verifyReferralCode(refCode);
      }
    };

    extractReferralFromUrl();
  }, [trackEvent]);

  // NEW: Handle direct JobProt signup with authorization code
  const handleDirectJobProtSignup = async (authCode: string, referralCode: string | null, state: string | null) => {
    setLoading(true);
    setDirectSignupInProgress(true);
    setErrorMessage(null);

    console.log('Starting direct JobProt signup flow...');

    // Track the automatic signup attempt
    if (trackEvent) {
      trackEvent('auto_signup_attempt', {
        method: 'direct_jobprot',
        has_referral: !!referralCode,
        referral_code: referralCode,
        has_state: !!state
      });
    }

    try {
      // Validate that we have a state parameter from JobProt
      if (!state) {
        console.warn('No state parameter provided by JobProt, generating fallback state');
        // Generate fallback state only if JobProt didn't provide one
        const fallbackState = 'direct_signup_' + Date.now();
        console.log('Using fallback state:', fallbackState);

        // Track missing state scenario
        if (trackEvent) {
          trackEvent('missing_state_parameter', {
            method: 'direct_jobprot',
            generated_fallback: true
          });
        }
      }

      console.log('Exchanging authorization code for tokens with state:', state || 'fallback');

      // Use the existing token exchange functionality with JobProt-provided state
      const response = await ssoService.exchangeToken({
        code: authCode,
        state: state || 'direct_signup_' + Date.now(), // Use provided state or fallback
        referralCode: referralCode || undefined
      });

      console.log("Direct signup token exchange successful:", response);

      // Extract user from response
      const user = response.user || response.data?.user;

      if (!user) {
        throw new Error("Missing user data in signup response");
      }

      // Track successful direct signup
      if (trackEvent) {
        trackEvent('direct_signup_success', {
          method: 'jobprot',
          new_user: user.newUser || false,
          has_referral: !!referralCode,
          used_provided_state: !!state
        });
      }

      // Clear referral code from URL and localStorage since it's been used
      if (referralCode) {
        localStorage.removeItem('referralCode');

        // Clean up URL without refreshing page
        const url = new URL(window.location.href);
        url.searchParams.delete('ref');
        url.searchParams.delete('code');
        url.searchParams.delete('source');
        url.searchParams.delete('state');
        window.history.replaceState({}, document.title, url.toString());
      }

      // Call onSSOSuccess with the response if provided
      if (onSSOSuccess) {
        // set response.user.newUser to true
        response.user.newUser = true;
        console.log("Direct signup success callback completed", response);
        await onSSOSuccess(response);
        console.log("Direct signup success callback completed");
      }

    } catch (error: any) {
      console.error('Direct JobProt signup failed:', error);

      // Track failed direct signup
      if (trackEvent) {
        trackEvent('direct_signup_failure', {
          method: 'jobprot',
          reason: error?.message || 'unknown_error',
          has_referral: !!referralCode,
          had_state: !!state
        });
      }

      // Set appropriate error message
      if (error?.message?.includes('Invalid authorization code')) {
        setErrorMessage('The signup link has expired or is invalid. Please try signing up again.');
      } else if (error?.message?.includes('Invalid state parameter')) {
        setErrorMessage('The signup link appears to be tampered with. Please try signing up again.');
      } else if (error?.message?.includes('already exists')) {
        setErrorMessage('An account with this email already exists. Please try logging in instead.');
      } else {
        setErrorMessage('Failed to complete signup. Please try the manual signup process.');
      }

      // Show toast message
      if (showToast) {
        showToast('Automatic signup failed. Please try the manual process.', 'error');
      }

      // Clean up URL parameters on error
      const url = new URL(window.location.href);
      url.searchParams.delete('ref');
      url.searchParams.delete('code');
      url.searchParams.delete('source');
      url.searchParams.delete('state');
      window.history.replaceState({}, document.title, url.toString());

    } finally {
      setLoading(false);
      setDirectSignupInProgress(false);
    }
  };

  const handleSignUp = async () => {
    // Track signup attempt
    if (trackButton) {
      trackButton('signup_button', 'login_page');
    }

    if (trackEvent) {
      trackEvent('signup_initiated', {
        method: 'jobprot',
        has_referral: !!referralInfo?.code,
        referrer: document.referrer || 'direct'
      });
    }

    // Preserve referral code if present
    // Open in same tab
    // if (referralInfo?.code) {
    //   window.location.href = `https://dashboard.jobprot.com/sign-up?mocktrail-post-signup-redirect=true&ref=${referralInfo.code}`;
    // } else {
    //   window.location.href = 'https://dashboard.jobprot.com/sign-up?mocktrail-post-signup-redirect=true';
    // }

    // First, initiate the JobProt SSO process to get the authorization URL
    const authResponse = await ssoService.initiateJobProtSignupSSO(referralInfo?.code);

    // Log the complete response to debug
    console.log("Signup response from ssoService:", authResponse);

    // Extract the URL and state from the response (will be available directly now)
    const { url, state } = authResponse;

    // Open in same tab
    window.location.href = url;

  };

  // Handle JobProt SSO
  const handleSSO = async () => {
    setLoading(true);
    setErrorMessage(null);
    setShowSafariPopupGuide(false);
    if (trackButton) {
      trackButton('sso_login_button', 'login_page');
    }
    if (trackEvent) {
      trackEvent('login_attempt', {
        method: 'sso',
        provider: 'jobprot'
      });
    }
    // --- AUTH ATTEMPT ---
    trackAuthAttempt('jobprot', 'login_page', {
      source: 'login_form',
      timestamp: new Date().toISOString()
    });
    try {
      // First, initiate the JobProt SSO process to get the authorization URL
      const authResponse = await ssoService.initiateJobProtSSO();

      // Log the complete response to debug
      console.log("Auth response from ssoService:", authResponse);

      // Extract the URL and state from the response (will be available directly now)
      const { url, state } = authResponse;

      if (!url || !state) {
        throw new Error('Invalid authentication response: Missing URL or state');
      }

      console.log("Opening SSO popup with URL:", url);

      // Open popup with the URL from the response
      const popupResponse = await ssoService.openSSOPopup(url);

      console.log("Received popup response:", JSON.stringify(popupResponse));
      console.log("Response structure:", {
        type: popupResponse.type,
        success: popupResponse.success,
        paramsSuccess: popupResponse.params.success,
        code: popupResponse.params.code ? `${popupResponse.params.code.substring(0, 10)}...` : null,
        state: popupResponse.params.state
      });

      // Make sure we have a code from the popup
      if (!popupResponse.params.code) {
        throw new Error('Authentication failed: No authorization code received');
      }

      // Get referral code from localStorage if it exists
      const storedReferralCode = localStorage.getItem('referralCode');
      console.log("Using stored referral code:", storedReferralCode);

      // Exchange code for token
      try {
        const response = await ssoService.exchangeToken({
          code: popupResponse.params.code,
          state: state, // Use the original state from initiateJobProtSSO
          referralCode: storedReferralCode || undefined // Pass referral code if it exists
        });

        console.log("Token exchange successful:", response);

        // Extract user from normalized response (could be at top level or in data property)
        const user = response.user || response.data?.user;

        // Validate that we have user data
        if (!user) {
          console.error("Missing user data in token exchange response:", response);
          throw new Error("Missing user data in authentication response");
        }

        // Track successful SSO login
        if (trackEvent) {
          trackEvent('login_success', {
            method: 'sso',
            provider: 'jobprot',
            new_user: user.newUser || false
          });
        }

        // Track successful authentication in Meta Pixel
        trackAuthSuccess('jobprot', 'login_page', {
          source: 'login_form',
          new_user: user.newUser || false,
          timestamp: new Date().toISOString()
        });
        logEvent(analytics, 'auth_success', {
          auth_method: 'jobprot',
          location: 'login_page',
          source: 'login_form',
          new_user: user.newUser || false,
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
          browser: detectBrowser(),
          device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
        });

        // If successful, clear the stored referral code as it's been used
        if (storedReferralCode) {
          localStorage.removeItem('referralCode');
        }

        // Call onSSOSuccess with the response if provided
        if (onSSOSuccess) {
          await onSSOSuccess(response);

          // Add debugging info
          console.log("SSO Success callback completed");
          console.log("Auth state after SSO:", {
            storeAuth: useAuthStore.getState().isAuthenticated,
            localStorageAuth: localStorage.getItem('isAuthenticated') === 'true',
            hasToken: !!localStorage.getItem('mocktrail-token')
          });
        }
      } catch (tokenError: any) {
        console.error('Token exchange failed:', tokenError);
        // Log detailed error information for debugging
        console.error('Token exchange error details:', {
          message: tokenError?.message,
          response: tokenError?.response,
          stack: tokenError?.stack
        });

        // Track token exchange failure
        if (trackEvent) {
          trackEvent('login_failure', {
            method: 'sso',
            provider: 'jobprot',
            stage: 'token_exchange',
            reason: tokenError?.message || 'unknown_error'
          });
        }
        logEvent(analytics, 'auth_failure', {
          auth_method: 'jobprot',
          location: 'login_page',
          error_type: 'token_exchange_failed',
          error_message: tokenError?.message || 'unknown_error',
          source: 'login_form',
          stage: 'token_exchange',
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
          browser: detectBrowser(),
          device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
        });

        setErrorMessage('Failed to complete authentication. Please try again.');

        if (showToast) {
          showToast('Failed to complete authentication. Please try again.', 'error');
        }
      }
    } catch (error: any) {
      console.error('JobProt SSO failed:', error);

      // Track failed SSO login
      if (trackEvent) {
        trackEvent('login_failure', {
          method: 'sso',
          provider: 'jobprot',
          stage: 'auth_flow',
          reason: error?.message || 'unknown_error'
        });
      }
      logEvent(analytics, 'auth_failure', {
        auth_method: 'jobprot',
        location: 'login_page',
        error_type: 'auth_flow_failed',
        error_message: error?.message || 'unknown_error',
        source: 'login_form',
        stage: 'auth_flow',
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: detectBrowser(),
        device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
      });

      // Handle popup blocked error specifically for Safari on Apple devices
      if (error?.message?.includes('Popup was blocked')) {
        // Track popup blocked event in Meta Pixel
        trackPopupBlocked('jobprot', 'login_page', {
          source: 'login_form',
          timestamp: new Date().toISOString(),
          error_type: 'popup_blocked',
          error_message: error.message,
          browser: isSafariApple() ? 'Safari' : 'Other'
        });
        logEvent(analytics, 'popup_blocked', {
          auth_method: 'jobprot',
          location: 'login_page',
          error_type: 'popup_blocked',
          error_message: error.message,
          source: 'login_form',
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
          browser: isSafariApple() ? 'Safari' : detectBrowser(),
          device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
        });
        if (isSafariApple()) {
          // Show Safari-specific popup guide instead of generic error
          setShowSafariPopupGuide(true);
          return; // Don't set generic error message
        } else {
          setErrorMessage('Please allow popups for this site to login with JobProt.');
          trackPopupBlocked('jobprot', 'login_page', {
            source: 'login_form',
            timestamp: new Date().toISOString(),
            error_type: 'popup_blocked_please_allow',
            error_message: error.message,
            browser: isSafariApple() ? 'Safari' : 'Other'
          });
          logEvent(analytics, 'popup_blocked', {
            auth_method: 'jobprot',
            location: 'login_page',
            error_type: 'popup_blocked_please_allow',
            error_message: error.message,
            source: 'login_form',
            timestamp: new Date().toISOString(),
            page_url: window.location.href,
            page_path: window.location.pathname,
            user_agent: navigator.userAgent,
            browser: isSafariApple() ? 'Safari' : detectBrowser(),
            device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
          });
        }
      } else if (error?.message?.includes('Authentication cancelled')) {
        setErrorMessage('Login was cancelled. Please try again.');
        trackAuthFailure('jobprot', 'login_page', 'auth_flow_failed', error?.message || 'unknown_error', {
          source: 'login_form',
          stage: 'auth_flow',
          timestamp: new Date().toISOString()
        });

        logEvent(analytics, 'auth_failure', {
          auth_method: 'jobprot',
          location: 'login_page',
          error_type: 'auth_flow_failed',
          error_message: error?.message || 'unknown_error',
          source: 'login_form',
          stage: 'auth_flow',
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
          browser: detectBrowser(),
          device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
        });
      } else if (error?.message?.includes('Invalid authentication response')) {
        setErrorMessage('Could not initialize authentication. Please try again later.');
        trackAuthFailure('jobprot', 'login_page', 'auth_flow_failed', error?.message || 'unknown_error', {
          source: 'login_form',
          stage: 'auth_flow',
          timestamp: new Date().toISOString()
        });
        
      } else if (error?.message?.includes('No authorization code')) {
        setErrorMessage('Authentication failed. No authorization code received.');
      } else {
        setErrorMessage('Failed to authenticate with JobProt. Please try again.');
      }

      // Show toast message if available (but not for Safari popup guide)
      if (showToast && !showSafariPopupGuide) {
        showToast('Failed to authenticate with JobProt. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  // Retry function for Safari popup guide
  const handleRetrySSO = () => {
    setShowSafariPopupGuide(false);
    handleSSO();
  };
  
  // Handle Google Sign-in
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setErrorMessage(null);
    setShowSafariPopupGuide(false);
    if (trackEvent) {
      trackEvent('google_signin_attempt', {
        source: 'mocktrail',
        has_referral: !!referralInfo?.code,
        is_mobile: isMobile,
        flow_type: isMobile ? 'redirect' : 'popup'
      });
    }
    // --- AUTH ATTEMPT ---
    trackAuthAttempt('google', 'login_page', {
      source: 'login_form',
      has_referral: !!referralInfo?.code,
      timestamp: new Date().toISOString()
    });
    logEvent(analytics, 'auth_attempt', {
      auth_method: 'google',
      location: 'login_page',
      source: 'login_form',
      has_referral: !!referralInfo?.code,
      timestamp: new Date().toISOString(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      user_agent: navigator.userAgent,
      browser: detectBrowser(),
      device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
    });
    try {
      // Initiate Google auth through our service
      const response = await googleAuthService.initiateGoogleAuth();
      
      console.log('Google auth completed:', response);
      
      // Extract user from response
      const user = response.user || response.data?.user;
      
      if (!user) {
        throw new Error('No user data received from Google authentication');
      }
      
      // Track successful Google sign-in
      if (trackEvent) {
        trackEvent('google_signin_success', {
          new_user: user.newUser || false,
          has_referral: !!referralInfo?.code
        });
      }

      // Track successful authentication in Meta Pixel
      trackAuthSuccess('google', 'login_page', {
        source: 'login_form',
        new_user: user.newUser || false,
        has_referral: !!referralInfo?.code,
        timestamp: new Date().toISOString()
      });
      logEvent(analytics, 'auth_success', {
        auth_method: 'google',
        location: 'login_page',
        source: 'login_form',
        new_user: user.newUser || false,
        has_referral: !!referralInfo?.code,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: detectBrowser(),
        device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
      });
      
      // Clear referral code if used
      if (referralInfo?.code) {
        localStorage.removeItem('referralCode');
      }
      
      // Call onSSOSuccess with the response
      if (onSSOSuccess) {
        await onSSOSuccess(response);
        console.log('Google sign-in success callback completed');
      }
    } catch (error: any) {
      console.error('Google sign-in failed:', error);
      
      // Track failure
      if (trackEvent) {
        trackEvent('google_signin_failure', {
          reason: error?.message || 'unknown_error'
        });
      }

      // Track authentication failure in Meta Pixel
      trackAuthFailure('google', 'login_page', 'google_auth_failed', error?.message || 'unknown_error', {
        source: 'login_form',
        has_referral: !!referralInfo?.code,
        timestamp: new Date().toISOString()
      });
      logEvent(analytics, 'auth_failure', {
        auth_method: 'google',
        location: 'login_page',
        error_type: 'google_auth_failed',
        error_message: error?.message || 'unknown_error',
        source: 'login_form',
        has_referral: !!referralInfo?.code,
        timestamp: new Date().toISOString(),
        page_url: window.location.href,
        page_path: window.location.pathname,
        user_agent: navigator.userAgent,
        browser: detectBrowser(),
        device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
      });
      
      // Handle specific errors
      if (error?.message?.includes('Popup was blocked')) {
        trackPopupBlocked('google', 'login_page', {
          source: 'login_form',
          timestamp: new Date().toISOString(),
          error_type: 'popup_blocked',
          error_message: error.message,
          browser: isSafariApple() ? 'Safari' : 'Other',
          has_referral: !!referralInfo?.code
        });
        logEvent(analytics, 'popup_blocked', {
          auth_method: 'google',
          location: 'login_page',
          error_type: 'popup_blocked',
          error_message: error.message,
          source: 'login_form',
          has_referral: !!referralInfo?.code,
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          page_path: window.location.pathname,
          user_agent: navigator.userAgent,
          browser: isSafariApple() ? 'Safari' : detectBrowser(),
          device_type: /Android/i.test(navigator.userAgent) ? 'Android' : /iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'iOS' : /Windows/i.test(navigator.userAgent) ? 'Windows' : /Mac/i.test(navigator.userAgent) ? 'Mac' : /Linux/i.test(navigator.userAgent) ? 'Linux' : 'Unknown'
        });
        if (isSafariApple()) {
          setShowSafariPopupGuide(true);
          return;
        } else {
          setErrorMessage('Please allow popups for this site to sign in with Google.');
        }
      } else if (error?.message?.includes('cancelled')) {
        setErrorMessage('Google sign-in was cancelled.');
      } else {
        setErrorMessage('Failed to sign in with Google. Please try again.');
      }
      
      if (showToast && !showSafariPopupGuide) {
        showToast('Failed to sign in with Google. Please try again.', 'error');
        setGoogleLoading(false);
      }
    } finally {
      setLoading(false);
      setGoogleLoading(false);
    }
  };

  return (
    <div className="relative h-full w-full">
      {/* Glass container without borders - scrollable on BOTH mobile AND desktop */}
      <div className={`relative h-full bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 backdrop-blur-md overflow-y-auto ${isMobile ? 'max-h-[90vh]' : ''}`}>

        {/* Content with proper vertical centering */}
        <div className={`flex flex-col items-center ${isMobile ? 'justify-start' : 'justify-center'} p-6 md:p-12 min-h-full`}>
          {/* Logo */}
          <div className={`mt-2 md:mt-0 ${isMobile ? 'mb-6' : 'mb-10'}`}>
            <Logo isCentered showText={true} />
          </div>

          {/* Welcome Text */}
          <div className="text-center mb-6">
            <h1 className="text-4xl font-heading font-bold mb-4 text-text-primary-light dark:text-text-primary-dark">
              {directSignupInProgress ? "Completing Your Signup..." : "Welcome to MockTrail"}
            </h1>
            {isWeekend ? (
              <p className="text-base font-medium text-text-secondary-light dark:text-text-secondary-dark max-w-md leading-relaxed">
                MockTrail is <span className="text-secondary-light dark:text-secondary-dark font-bold font-heading">100% FREE</span> on Weekends. Sign up to practice with our AI Coach - No limits, pure practice!
              </p>
            ) : (
              <p className="text-base font-medium text-text-secondary-light dark:text-text-secondary-dark max-w-md leading-relaxed">
                Practice <span className="text-text-primary-light dark:text-text-primary-dark">Smarter, not Harder</span> with MockTrail's AI Coach. Sign in to start your journey.
              </p>
            )}
          </div>
          

          {/* Error Message - Display if error exists */}
          {errorMessage && (
            <div className="w-full flex flex-row max-w-sm mb-4 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 rounded-lg p-3 border border-red-200 dark:border-red-800/50 text-sm">
              <AlertTriangleIcon className="text-red-500 dark:text-red-400 mr-2" size={20} />
              {errorMessage}
            </div>
          )}

          {/* Safari Popup Guide - Show instead of error for Safari popup blocking */}
          {showSafariPopupGuide && (
            <div className="mb-4">
              <SafariPopupGuide onRetry={handleRetrySSO} />
            </div>
          )}

          {/* Referral Banner - Show only if referral code is present */}
          {referralInfo && (
            <div className={`w-full max-w-sm mb-6 ${referralInfo.isValid === true ? 'bg-primary-light/10 dark:bg-primary-dark/10' : 'bg-red-500/10 dark:bg-red-500/10'} rounded-lg p-4 border ${referralInfo.isValid === true ? 'border-primary-light/20 dark:border-primary-dark/20' : 'border-red-500/20 dark:border-red-500/20'}`}>
              <div className="flex flex-col items-center text-center">
                <Gift className={`${referralInfo.isValid === true ? 'text-primary-light dark:text-primary-dark' : 'text-red-500 dark:text-red-400'} mb-2`} size={24} />
                <div>
                  <h3 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                    {referralInfo.isValid === true ? "You've been invited!" : "Invalid referral code"}
                  </h3>
                  {referralLoading ? (
                    <div className="flex items-center justify-center mt-1 text-text-secondary-light dark:text-text-secondary-dark">
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      <span>Verifying invitation...</span>
                    </div>
                  ) : (
                    <p className="text-sm mt-1 text-text-secondary-light dark:text-text-secondary-dark">
                      {referralInfo.name && referralInfo.isValid === true
                        ? `${referralInfo.name} invited you to MockTrail. Sign up to get free interview minutes!`
                        : `The code "${referralInfo.code}" was not recognized, you can still sign up.`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div className="w-full max-w-sm">
            {/* Google Sign-in Button */}
            <GoogleSignInButton
              onClick={handleGoogleSignIn}
              loading={googleLoading}
              disabled={directSignupInProgress || processingOAuthCallback}
            />

            {/* OR Divider */}
            <div className="relative w-full my-4 flex flex-row items-center">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
              </div>
              <div className="relative flex justify-center text-sm mx-auto">
                <span className="px-3 bg-gradient-to-br from-background-light/80 to-surface-light/80 dark:from-background-dark/80 dark:to-surface-dark/80 text-text-secondary-light dark:text-text-secondary-dark">Or</span>
              </div>
            </div>
            </div>
          {/* Buttons container with proper spacing */}
          <div className="w-full max-w-sm space-y-6 mb-8 flex flex-col items-center">

            {/* Primary Button - Login with JobProt */}
            <Button
              variant="primary"
              fullWidth
              size="lg"
              loading={loading && !errorMessage}
              disabled={directSignupInProgress || processingOAuthCallback}
              onClick={handleSSO}
              leftIcon={<UserRound size={isMobile ? 16 : 18} className="transition-transform duration-300 group-hover:scale-110" />}
            >
              <span className={`${isMobile ? 'text-lg' : 'text-lg'}`}>
                {directSignupInProgress || processingOAuthCallback ? 'Processing...' : 'Sign in with JobProt'}
              </span>
            </Button>

            {/* Create an account */}
            <div className="mt-8 w-full">
              <p className="text-sm text-center text-text-secondary-light dark:text-text-secondary-dark mb-3">
                Don't have an account? Sign up & claim your <span className="text-text-primary-light dark:text-text-primary-dark">free interview minutes!</span>
              </p>
              {/* Secondary Button - Create Account */}
              <Button
                variant="secondary"
                fullWidth
                size="md"
                disabled={directSignupInProgress}
                onClick={handleSignUp}
                rightIcon={<ExternalLink size={18} className="transition-transform duration-300 group-hover:scale-110" />}
              >
                Create an account
              </Button>
            </div>
          </div>

          {/* Security Note - Changed to info badge style */}
          <div className="inline-flex items-center gap-2 mb-8 text-sm text-text-tertiary-light dark:text-text-tertiary-dark px-3 py-1.5 rounded-full border border-border-light/10 dark:border-border-dark/10">
            <Lock size={14} className="text-primary-light dark:text-primary-dark" />
            <span>Secure sign-in with JobProt</span>
          </div>

          {/* Powered By */}
          <div className="pb-12 md:pb-0 mt-auto text-center relative before:absolute before:inset-x-0 before:top-[-1.5rem] before:h-[1px] before:bg-gradient-to-r before:from-transparent before:via-border-light/70 before:to-transparent dark:before:via-border-dark/70">
            <div className="text-text-secondary-light dark:text-text-secondary-dark text-xs mb-2">
              Powered by
            </div>
            <JobProtLogo size="md" />
          </div>
        </div>
      </div>
    </div>
  );
} 